import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, Subject, takeUntil } from 'rxjs';
import { DoesVarenrExistsResponseDto } from 'src/app/api/models/OK/Egis/Service/G4Communication/DTO/does-varenr-exists-response-dto';
import { PartnerVarenrMappingDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-varenr-mapping-dto';
import { VareService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { PartnerVarenrMappingDialogData } from 'src/app/components/aftale/models';

@Component({
  selector: 'app-partnervarenr-edit-dialog',
  templateUrl: './partnervarenr-edit-dialog.component.html',
  styleUrls: ['./partnervarenr-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PartnervarenrEditDialogComponent implements OnInit {
  partnerVarenrMapping: PartnerVarenrMappingDto;
  form: UntypedFormGroup;
  vareInfoCollected: boolean = false;
  noVareInfoInG4: boolean = false;
  unsubscribe$ = new Subject<void>();

  get okKundeVarenr() {
    return this.form.get('okKundeVarenr');
  }

  get partnerVarenr() {
    return this.form.get('partnerVarenr');
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private service: VareService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    public dialogRef: MatDialogRef<PartnervarenrEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PartnerVarenrMappingDialogData,
  ) {
    this.partnerVarenrMapping = data.partnerVarenrMapping;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      partnerVarenr: [this.partnerVarenrMapping.partnerVarenr, [Validators.required]],
      okKundeVarenr: [this.partnerVarenrMapping.okKundeVarenr, [Validators.required]],
    });
  }

  async do() {
    if (!this.form.valid) return;
    if (this.partnerVarenrMapping.id) {
      await this.update();
    } else {
      await this.create();
    }
  }

  async create() {
    let data: PartnerVarenrMappingDto = {
      okKundeVarenr: this.okKundeVarenr.value,
      partnerVarenr: this.partnerVarenr.value,
    };

    data.okKundeVarenr = this.okKundeVarenr.value;
    this.loader.show();
    await this.service
      .createPartnerVareNrMapping({ caller: EGIS_SERVICE_CALLER, body: data })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (result: PartnerVarenrMappingDto) => {
          this.dialogRef.close(result);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  async update() {
    let data: PartnerVarenrMappingDto = {
      id: this.partnerVarenrMapping.id,
      okKundeVarenr: this.okKundeVarenr.value,
      partnerVarenr: this.partnerVarenr.value,
    };

    this.loader.show();
    await this.service
      .updatePartnerVareNrMapping({
        caller: EGIS_SERVICE_CALLER,
        id: this.partnerVarenrMapping.id,
        body: data,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (result: PartnerVarenrMappingDto) => {
          this.dialogRef.close(result);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  getInfoForVareNr(vareNr: number) {
    this.service
      .doesVareNrExist({
        caller: EGIS_SERVICE_CALLER,
        vareNr: vareNr,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (vareInfo: DoesVarenrExistsResponseDto) => {
          this.noVareInfoInG4 = !(vareInfo && vareInfo.doesVarenrExists);
          this.vareInfoCollected = !this.noVareInfoInG4;
          if (this.vareInfoCollected) {
            this.okKundeVarenr.disable();
          }
        },
        (error: HttpErrorResponse) => {
          this.vareInfoCollected = false;
          this.errorHandler.HandleError(error);
        },
      );
  }

  clearVareInfo() {
    this.vareInfoCollected = false;
    this.noVareInfoInG4 = false;
    this.okKundeVarenr.setValue(null);
    this.okKundeVarenr.enable();
  }
}
